import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOALS = [
  "Lose weight / Get lean",
  "Build strength & muscle",
  "Soccer performance",
  "Youth / kid athlete",
  "Online coaching",
  "General fitness",
];

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    goal: GOALS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, form);
      toast.success(res.data.message || "Thanks! Coach Manny will reach out.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", goal: GOALS[0], message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Couldn't send right now. Please call (208) 901-9386.";
      toast.error(typeof detail === "string" ? detail : "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="lead-form-section"
      className="section border-t border-white/10 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/0a4872c7-0ac7-41c4-a84c-97ee9bda904d/images/93d4e2e41db2f799ab3dc800d6d36c81b5eeb148e880caaa43e66b1301f27a6f.png"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
      </div>

      <div className="relative container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#FF3B30] mb-4">
            / 04 — Claim your free session
          </div>
          <h2
            data-testid="lead-form-title"
            className="font-display text-5xl md:text-6xl uppercase leading-[0.95] tracking-tight"
          >
            Your first
            <br />
            session is
            <br />
            <span className="text-[#FF3B30]">on the house.</span>
          </h2>
          <p className="text-white/70 mt-6 text-lg leading-relaxed max-w-md">
            New clients get one full session with Coach Manny — completely free.
            Tell him a little about your goals and he'll reach out within 24
            hours to lock it in.
          </p>
          <div className="mt-10 space-y-3 text-sm text-white/70">
            <div>
              <span className="text-white/50 uppercase tracking-widest text-[10px] block mb-1">
                Prefer a call?
              </span>
              <a
                href="tel:+12089019386"
                data-testid="lead-form-phone"
                className="font-display text-3xl text-white hover:text-[#FF3B30] transition-colors"
              >
                (208) 901-9386
              </a>
            </div>
            <div>
              <span className="text-white/50 uppercase tracking-widest text-[10px] block mb-1">
                Or WhatsApp
              </span>
              <a
                href="https://wa.me/12089019386"
                target="_blank"
                rel="noreferrer"
                data-testid="lead-form-whatsapp"
                className="text-white/80 underline underline-offset-4 hover:text-[#25D366]"
              >
                Chat on WhatsApp →
              </a>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="lead-form"
          className="lg:col-span-7 bg-[#111] border border-white/10 p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50 block mb-2">
                Full name
              </label>
              <input
                data-testid="input-name"
                type="text"
                className="field-input"
                placeholder="Your name"
                value={form.name}
                onChange={update("name")}
                required
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50 block mb-2">
                Email
              </label>
              <input
                data-testid="input-email"
                type="email"
                className="field-input"
                placeholder="you@email.com"
                value={form.email}
                onChange={update("email")}
                required
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50 block mb-2">
                Phone
              </label>
              <input
                data-testid="input-phone"
                type="tel"
                className="field-input"
                placeholder="(___) ___-____"
                value={form.phone}
                onChange={update("phone")}
                required
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50 block mb-2">
                Primary goal
              </label>
              <select
                data-testid="input-goal"
                className="field-input bg-[#111] appearance-none cursor-pointer"
                value={form.goal}
                onChange={update("goal")}
              >
                {GOALS.map((g) => (
                  <option key={g} value={g} className="bg-[#111]">
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50 block mb-2">
                Tell Coach Manny a bit more (optional)
              </label>
              <textarea
                data-testid="input-message"
                rows={4}
                className="field-input resize-none"
                placeholder="Current fitness level, schedule, anything you want him to know…"
                value={form.message}
                onChange={update("message")}
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <p className="text-xs text-white/50 max-w-xs">
              By submitting, you agree to be contacted about training. We never
              share your info.
            </p>
            <button
              type="submit"
              disabled={loading || done}
              data-testid="submit-lead-btn"
              className="btn-primary disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : done ? (
                <>Request Sent ✓</>
              ) : (
                <>
                  Claim Free Session <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
